import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Badge, DataTable, statusColor, type ColumnDef, type BadgeColor } from "@/components/DataTable";
import { Modal } from "@/components/Modal";
import {
  listMerchantTransactions,
  type MerchantTransaction,
} from "@/lib/api";
import { formatDate, formatMajor, formatRelative } from "@/lib/format";
import { ArrowDownRight, ArrowUpRight, Eye, Download, Activity, CheckCircle2, XCircle, Clock } from "lucide-react";

function statusFromPos(s: string): BadgeColor {
  const v = s?.toLowerCase();
  if (v === "successful" || v === "success" || v === "completed") return "success";
  if (v === "failed" || v === "rejected") return "danger";
  if (v === "pending") return "warning";
  return statusColor(v);
}

function methodColor(method: string): BadgeColor {
  const v = method?.toLowerCase();
  if (v === "mpesa" || v === "afrimoney") return "primary";
  if (v === "orange") return "warning";
  if (v === "airtel") return "info";
  return "light";
}

export function TransactionsPage() {
  const auth = useAuth();
  const merchantId = auth.status === "authenticated" ? auth.user.merchant_id : null;

  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [methodFilter, setMethodFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<"all" | "depot" | "retrait">("all");
  const [currencyFilter, setCurrencyFilter] = useState<"all" | "CDF" | "USD">("all");
  const [selected, setSelected] = useState<MerchantTransaction | null>(null);

  const query = useQuery<MerchantTransaction[]>({
    queryKey: ["merchant-transactions", merchantId],
    queryFn: listMerchantTransactions,
    enabled: !!merchantId,
  });

  const transactions = query.data ?? [];

  const filtered = useMemo(() => {
    return transactions.filter((tx) => {
      if (statusFilter !== "all" && tx.status !== statusFilter) return false;
      if (methodFilter !== "all" && tx.method !== methodFilter) return false;
      if (typeFilter !== "all" && tx.operation_type !== typeFilter) return false;
      if (currencyFilter !== "all" && tx.currency !== currencyFilter) return false;
      return true;
    });
  }, [transactions, statusFilter, methodFilter, typeFilter, currencyFilter]);

  const stats = useMemo(() => {
    const acc = { total: 0, success: 0, failed: 0, pending: 0 };
    for (const tx of filtered) {
      acc.total += 1;
      const s = tx.status?.toLowerCase();
      if (s === "successful" || s === "success" || s === "completed") acc.success += 1;
      else if (s === "failed" || s === "rejected") acc.failed += 1;
      else if (s === "pending") acc.pending += 1;
    }
    return acc;
  }, [filtered]);

  const uniqueMethods = useMemo(
    () => Array.from(new Set(transactions.map((tx) => tx.method))).filter(Boolean).sort(),
    [transactions],
  );

  const columns: ColumnDef<MerchantTransaction>[] = [
    {
      key: "operation_type",
      header: "Type",
      minWidth: 90,
      accessor: (r) => r.operation_type,
      cell: (r) =>
        r.operation_type === "retrait" ? (
          <span
            className="inline-flex items-center gap-1 text-xs font-semibold text-info-700 bg-info-50 px-2 py-0.5 rounded"
            title="Retrait (débit du wallet marchand)"
          >
            <ArrowUpRight size={12} />
            Retrait
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1 text-xs font-semibold text-success-700 bg-success-50 px-2 py-0.5 rounded"
            title="Dépôt (crédit du wallet marchand)"
          >
            <ArrowDownRight size={12} />
            Dépôt
          </span>
        ),
    },
    {
      key: "reference",
      header: "Référence",
      minWidth: 220,
      accessor: (r) => r.reference,
      sortable: true,
      wrap: true,
      cell: (r) => (
        <div>
          <div className="font-mono text-xs text-[#181c32] font-semibold">{r.reference}</div>
          {r.telco_reference && (
            <div className="text-[10px] text-[#a1a5b7] mt-0.5" title="Référence telco">
              telco : {r.telco_reference}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "amount",
      header: "Montant",
      align: "right",
      minWidth: 110,
      sortable: true,
      accessor: (r) => Number(r.amount ?? 0),
      cell: (r) => (
        <span className="font-semibold text-[#181c32]">
          {formatMajor(Number(r.amount ?? 0), r.currency).replace(` ${r.currency}`, "")}
        </span>
      ),
    },
    {
      key: "fee",
      header: "Frais",
      align: "right",
      minWidth: 90,
      sortable: true,
      accessor: (r) => Number(r.fee ?? 0),
      cell: (r) => {
        const fee = Number(r.fee ?? 0);
        if (fee === 0) return <span className="text-xs text-[#a1a5b7]">—</span>;
        return (
          <span className="text-xs text-[#7e8299]">
            {formatMajor(fee, r.currency).replace(` ${r.currency}`, "")}
          </span>
        );
      },
    },
    {
      key: "currency",
      header: "Devise",
      minWidth: 70,
      sortable: true,
      accessor: (r) => r.currency,
      cell: (r) => <span className="text-sm font-semibold text-[#3f4254]">{r.currency}</span>,
    },
    {
      key: "method",
      header: "Méthode",
      minWidth: 100,
      sortable: true,
      accessor: (r) => r.method,
      cell: (r) => <Badge color={methodColor(r.method)}>{r.method}</Badge>,
    },
    {
      key: "customer_number",
      header: "Client",
      minWidth: 130,
      accessor: (r) => r.customer_number ?? "",
      cell: (r) => (
        <span className="text-xs text-[#3f4254] font-mono">{r.customer_number ?? "—"}</span>
      ),
    },
    {
      key: "status",
      header: "Statut",
      minWidth: 110,
      sortable: true,
      accessor: (r) => r.status,
      cell: (r) => <Badge color={statusFromPos(r.status)}>{r.status}</Badge>,
    },
    {
      key: "created_at",
      header: "Date",
      minWidth: 150,
      sortable: true,
      accessor: (r) => r.created_at,
      cell: (r) => (
        <div>
          <div className="text-xs text-[#3f4254]">{formatDate(r.created_at)}</div>
          <div className="text-[10px] text-[#a1a5b7]">{formatRelative(r.created_at)}</div>
        </div>
      ),
    },
    {
      key: "actions",
      header: "",
      minWidth: 40,
      cell: (r) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setSelected(r);
          }}
          className="text-[#a1a5b7] hover:text-brand-600"
          title="Détail"
        >
          <Eye size={14} />
        </button>
      ),
    },
  ];

  const filters = (
    <>
      <select
        value={typeFilter}
        onChange={(e) => setTypeFilter(e.target.value as typeof typeFilter)}
        className="form-input py-2 px-3 text-sm w-auto"
      >
        <option value="all">Tous types</option>
        <option value="depot">Dépôts</option>
        <option value="retrait">Retraits</option>
      </select>
      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        className="form-input py-2 px-3 text-sm w-auto"
      >
        <option value="all">Tous statuts</option>
        <option value="Successful">Successful</option>
        <option value="Pending">Pending</option>
        <option value="Failed">Failed</option>
      </select>
      <select
        value={methodFilter}
        onChange={(e) => setMethodFilter(e.target.value)}
        className="form-input py-2 px-3 text-sm w-auto"
      >
        <option value="all">Toutes méthodes</option>
        {uniqueMethods.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
      <select
        value={currencyFilter}
        onChange={(e) => setCurrencyFilter(e.target.value as typeof currencyFilter)}
        className="form-input py-2 px-3 text-sm w-auto"
      >
        <option value="all">Toutes devises</option>
        <option value="CDF">CDF</option>
        <option value="USD">USD</option>
      </select>
    </>
  );

  return (
    <div>
      <PageHeader
        title="Historique des transactions"
        breadcrumbs={[{ label: "Transactions" }, { label: "Historique" }]}
        actions={
          <button
            type="button"
            className="btn btn-light btn-sm"
            title="L'export XLSX/PDF sera branché dans un second temps."
            disabled
          >
            <Download size={14} />
            Exporter
          </button>
        }
      />

      {/* KPI mini-cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <StatMini label="Total" value={stats.total} icon={<Activity size={14} />} color="primary" />
        <StatMini
          label="Succès"
          value={stats.success}
          icon={<CheckCircle2 size={14} />}
          color="success"
        />
        <StatMini
          label="En attente"
          value={stats.pending}
          icon={<Clock size={14} />}
          color="warning"
        />
        <StatMini label="Échecs" value={stats.failed} icon={<XCircle size={14} />} color="danger" />
      </div>

      <DataTable<MerchantTransaction>
        title="Transactions du marchand"
        subtitle="Recherche libre par référence, client, ou n'importe quelle colonne triable"
        searchable
        searchPlaceholder="Rechercher (référence, téléphone client, référence telco…)"
        columns={columns}
        data={filtered}
        rowKey={(r) => `${r.operation_type}-${r.id}`}
        onRowClick={(r) => setSelected(r)}
        loading={query.isLoading}
        filtersSlot={filters}
        emptyMessage="Aucune transaction sur les filtres actuels"
        initialSort={{ key: "created_at", dir: "desc" }}
      />

      {selected && (
        <Modal open onClose={() => setSelected(null)} title="Détail transaction" size="lg">
          <TransactionDetail tx={selected} />
        </Modal>
      )}
    </div>
  );
}

function StatMini({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: "primary" | "success" | "warning" | "danger";
}) {
  const cls: Record<typeof color, string> = {
    primary: "text-brand-600 bg-brand-50",
    success: "text-success-600 bg-success-50",
    warning: "text-warning-700 bg-warning-50",
    danger: "text-danger-600 bg-danger-50",
  };
  return (
    <div className="card card-body py-3">
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${cls[color]}`}>
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-[#7e8299] font-bold">{label}</div>
          <div className="text-lg font-bold text-[#181c32] leading-tight">{value.toLocaleString("fr-FR")}</div>
        </div>
      </div>
    </div>
  );
}

function TransactionDetail({ tx }: { tx: MerchantTransaction }) {
  const rows: Array<[string, React.ReactNode]> = [
    ["Type", tx.operation_type === "retrait" ? "Retrait" : "Dépôt"],
    ["Statut", <Badge color={statusFromPos(tx.status)}>{tx.status}</Badge>],
    ["Référence", <span className="font-mono text-xs">{tx.reference}</span>],
    ["Référence telco", <span className="font-mono text-xs">{tx.telco_reference ?? "—"}</span>],
    ["Méthode", <Badge color={methodColor(tx.method)}>{tx.method}</Badge>],
    ["Client", <span className="font-mono text-xs">{tx.customer_number ?? "—"}</span>],
    ["Montant", <span className="font-semibold">{formatMajor(Number(tx.amount ?? 0), tx.currency)}</span>],
    ["Frais", <span className="text-[#7e8299]">{formatMajor(Number(tx.fee ?? 0), tx.currency)}</span>],
    ["Total", <span className="font-semibold text-[#181c32]">{formatMajor(Number(tx.total_amount ?? 0), tx.currency)}</span>],
    ["Description marchand", tx.description ?? <span className="text-[#a1a5b7]">—</span>],
    [
      "Description système",
      tx.status_description ? (
        <span className={`text-xs ${
          tx.status?.toLowerCase() === "successful" ? "text-success-700" : "text-danger-700"
        }`}>
          {tx.status_description}
        </span>
      ) : (
        <span className="text-[#a1a5b7]">—</span>
      ),
    ],
    ["Créé le", formatDate(tx.created_at)],
    ["Mis à jour", formatDate(tx.updated_at)],
  ];

  return (
    <div className="grid grid-cols-1 gap-2">
      {rows.map(([label, val]) => (
        <div
          key={label}
          className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-x-4 gap-y-1 py-2 border-b border-slate-100 last:border-0"
        >
          <div className="text-xs uppercase tracking-wider text-[#7e8299] font-bold">{label}</div>
          <div className="text-sm text-[#181c32]">{val}</div>
        </div>
      ))}
    </div>
  );
}
